import React from "react";
import { Row, Col, Card, Skeleton, Typography, DatePicker, Tooltip } from "antd";
import {
  ExperimentOutlined,
  AimOutlined,
  StarFilled,
  RiseOutlined,
  FallOutlined,
  FireOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  ArrowRightOutlined,
  InfoCircleOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { Title } from "components/common";
import styles from "./DashboardKPIsV2.module.less";

const { Text } = Typography;
const { RangePicker } = DatePicker;

// ── Date presets ───────────────────────────────────────────────────────────
const DATE_PRESETS = {
  "This Week":     [moment().startOf("week"),                      moment().endOf("week")],
  "This Month":    [moment().startOf("month"),                     moment().endOf("month")],
  "Last 3 Months": [moment().subtract(3, "months").startOf("day"), moment().endOf("day")],
  "Last 6 Months": [moment().subtract(6, "months").startOf("day"), moment().endOf("day")],
};

// ── Helpers ────────────────────────────────────────────────────────────────
const getAccuracyColor    = (v) => v == null ? "#1a1a1a" : v >= 70 ? "#16a34a" : v >= 40 ? "#d97706" : "#dc2626";
const getAccuracyBarColor = (v) => !v ? "#e2e8f0" : v >= 70 ? "#4ade80" : v >= 40 ? "#fbbf24" : "#f87171";
const getAccuracyStroke   = (v) => !v ? "#e2e8f0" : v >= 70 ? "#16a34a" : v >= 40 ? "#d97706" : "#dc2626";

const DAY_SHORT = ["S", "M", "T", "W", "T", "F", "S"];

// ── SVG circle gauge (accuracy card right panel) ───────────────────────────
const CircleGauge = ({ pct = 0, color = "#16a34a", size = 58 }) => {
  const r  = 21;
  const c  = 2 * Math.PI * r;
  const pctClamped = Math.min(Math.max(pct, 0), 100);
  const offset = c - (pctClamped / 100) * c;
  return (
    <svg width={size} height={size} viewBox="0 0 50 50" style={{ display: "block" }}>
      <circle cx="25" cy="25" r={r} fill="none" stroke="#f0f5ff" strokeWidth="4" />
      <circle
        cx="25" cy="25" r={r}
        fill="none" stroke={color} strokeWidth="4"
        strokeDasharray={c}
        strokeDashoffset={offset}
        strokeLinecap="round"
        transform="rotate(-90 25 25)"
        style={{ transition: "stroke-dashoffset 0.9s ease" }}
      />
      <text x="25" y="30" textAnchor="middle" fontSize="10" fontWeight="800" fill={color}>
        {pctClamped > 0 ? `${Math.round(pctClamped)}%` : "—"}
      </text>
    </svg>
  );
};

// PASS_THRESHOLD — quizzes at or above this accuracy count as "passing"
const PASS_THRESHOLD = 60;

// Recent quiz pass/fail dots (streak card right panel)
const QuizResultDots = ({ recentResults = [] }) => (
  <div className={styles.dotsWrap}>
    {recentResults.map((passed, i) => (
      <Tooltip key={i} title={`Quiz ${recentResults.length - i}: ${passed ? "Passed ✓" : "Failed ✗"}`}>
        <div className={`${styles.dot} ${passed ? styles.dotPass : styles.dotFail}`} />
      </Tooltip>
    ))}
  </div>
);

// ── Accuracy trend direction (body secondary info) ─────────────────────────
const AccuracyDirection = ({ direction }) => {
  if (direction === "up")
    return <span className={`${styles.secondaryInfo} ${styles.up}`}><ArrowUpOutlined />Trending up</span>;
  if (direction === "down")
    return <span className={`${styles.secondaryInfo} ${styles.down}`}><ArrowDownOutlined />Declining</span>;
  return <span className={`${styles.secondaryInfo} ${styles.neutral}`}><ArrowRightOutlined />Stable</span>;
};

// ── Per-card right panel ───────────────────────────────────────────────────
const KpiRightPanel = ({ kpi, raw, enrichData }) => {
  // Total Quizzes → "X this month"
  if (kpi.key === "totalQuizzes") {
    const count = enrichData?.recentQuizCount ?? 0;
    return (
      <>
        <div className={styles.kpiRightBigNum} style={{ color: "#4b93ff" }}>
          {count > 0 ? count : "—"}
        </div>
        <div className={styles.kpiRightLabel}>this month</div>
      </>
    );
  }

  // Overall Accuracy → circle gauge
  if (kpi.key === "overallAccuracy") {
    const pct = parseFloat(raw) || 0;
    return <CircleGauge pct={pct} color={getAccuracyStroke(pct)} />;
  }

  // Average Score → best / worst from recent quizzes
  if (kpi.key === "averageScore") {
    const high = enrichData?.scoreHigh;
    const low  = enrichData?.scoreLow;
    return (
      <div className={styles.kpiRightStatGroup}>
        <div className={styles.kpiRightStatRow}>
          <div className={styles.kpiRightStatVal} style={{ color: "#16a34a" }}>
            {high != null ? `${high.toFixed(0)}%` : "—"}
          </div>
          <div className={styles.kpiRightStatLabel}>Best</div>
        </div>
        <div className={styles.kpiRightStatRow}>
          <div className={styles.kpiRightStatVal} style={{ color: "#dc2626" }}>
            {low != null ? `${low.toFixed(0)}%` : "—"}
          </div>
          <div className={styles.kpiRightStatLabel}>Worst</div>
        </div>
      </div>
    );
  }

  // Strongest / Weakest specialty → big accuracy %
  if (kpi.key === "strongestSpeciality" || kpi.key === "weakestSpeciality") {
    const accMap = enrichData?.specialityAccuracyMap ?? {};
    const acc    = raw ? accMap[raw] : null;
    const color  = kpi.key === "strongestSpeciality" ? "#16a34a" : "#dc2626";
    return (
      <>
        <div className={styles.kpiRightBigNum} style={{ color }}>
          {acc != null ? `${acc.toFixed(0)}%` : "—"}
        </div>
        <div className={styles.kpiRightLabel}>accuracy</div>
      </>
    );
  }

  // Passing Quiz Streak → recent quiz pass/fail dots
  if (kpi.isStreak) {
    return (
      <>
        <QuizResultDots recentResults={enrichData?.recentQuizResults ?? []} />
        <div className={styles.kpiRightLabel} style={{ marginTop: 4 }}>last quizzes</div>
      </>
    );
  }

  return null;
};

// ── Single KPI card ────────────────────────────────────────────────────────
const KpiCard = ({ kpi, value, enrichData }) => {
  const raw         = value;
  const formatted   = kpi.format(raw);
  const valueColor  = kpi.colorize ? getAccuracyColor(raw) : kpi.isStreak ? "#ea580c" : "#1a1a1a";
  const accuracyPct = kpi.showProgressBar ? Math.min(parseFloat(raw) || 0, 100) : 0;

  return (
    <Card
      className={`${styles.kpiCard} ${kpi.isStreak ? styles.kpiCardStreak : ""}`}
      bordered
      style={{ borderTopColor: kpi.accentColor }}
    >
      <div className={styles.kpiInner}>
        {/* Icon */}
        <div className={styles.kpiIconWrap} style={{ background: kpi.iconBg, color: kpi.iconColor }}>
          {kpi.icon}
        </div>

        {/* Body */}
        <div className={styles.kpiBody}>
          <div className={styles.kpiLabel}>{kpi.label}</div>

          <div
            className={`${styles.kpiValue} ${kpi.smallValue ? styles.kpiValueSmall : ""}`}
            style={{ color: valueColor }}
          >
            {formatted}
          </div>

          {kpi.showProgressBar && (
            <div className={styles.accuracyBarWrap}>
              <div
                className={styles.accuracyBar}
                style={{ width: `${accuracyPct}%`, background: getAccuracyBarColor(parseFloat(raw)) }}
              />
            </div>
          )}

          {kpi.showAccuracyDirection && (
            <AccuracyDirection direction={enrichData?.accuracyDirection} />
          )}

          {kpi.showRecentCount && (
            <span className={styles.secondaryInfo}>
              <CalendarOutlined />
              {(enrichData?.recentQuizCount ?? 0) > 0
                ? `${enrichData.recentQuizCount} in last 30 days`
                : "No quizzes this month"}
            </span>
          )}

          {kpi.showSpecialtyCounts && raw && enrichData?.specialityCountsMap?.[raw] && (() => {
            const { correct, incorrect, total } = enrichData.specialityCountsMap[raw];
            const correctPct = total > 0 ? (correct / total) * 100 : 0;
            return (
              <>
                <div className={styles.specialtyStackBar}>
                  <div className={styles.specialtyStackCorrect} style={{ width: `${correctPct}%` }} />
                  <div className={styles.specialtyStackIncorrect} />
                </div>
                <div className={styles.specialtyCounts}>
                  <span className={styles.countCorrect}>{correct} correct</span>
                  <span className={styles.countSep}>·</span>
                  <span className={styles.countIncorrect}>{incorrect} incorrect</span>
                  <span className={styles.countSep}>·</span>
                  <span style={{ color: "#94a3b8", fontSize: 10.5 }}>{total} total</span>
                </div>
              </>
            );
          })()}

          {kpi.showTotalContext && enrichData?.totalQuizzes > 0 && (
            <span className={styles.secondaryInfo}>
              Based on {enrichData.totalQuizzes} quizzes
            </span>
          )}

          {kpi.isStreak && (
            <span className={`${styles.secondaryInfo} ${enrichData?.passStreak > 0 ? styles.up : styles.neutral}`}>
              {enrichData?.passStreak > 0 ? <ArrowUpOutlined /> : <ArrowRightOutlined />}
              {enrichData?.passStreak > 0
                ? `≥${PASS_THRESHOLD}% accuracy required`
                : "Score ≥60% to start a streak"}
            </span>
          )}

          <div className={styles.kpiFooter}>
            <Text className={styles.kpiDesc}>{kpi.desc}</Text>
          </div>
        </div>

        {/* Right visual panel */}
        <div className={styles.kpiRight}>
          <KpiRightPanel kpi={kpi} raw={raw} enrichData={enrichData} />
        </div>
      </div>
    </Card>
  );
};

// ── KPI configs ────────────────────────────────────────────────────────────
// ── Row 1: Core stats    — how much | how well | how high/low?
// ── Row 2: Insights      — best area | worst area | am I on a roll?
const METRICS_KPIs = [
  {
    key: "totalQuizzes",
    label: "Total Quizzes Attempted",
    desc: "All-time count",
    icon: <ExperimentOutlined />,
    iconBg: "#eff6ff", iconColor: "#4b93ff", accentColor: "#4b93ff",
    format: (v) => v ?? "—",
    showRecentCount: true,
  },
  {
    key: "overallAccuracy",
    label: "Overall Accuracy",
    desc: "Correct / Total",
    icon: <AimOutlined />,
    iconBg: "#f0fdf4", iconColor: "#16a34a", accentColor: "#16a34a",
    format: (v) => v != null ? `${v}%` : "—",
    colorize: true,
    // showProgressBar: true,
    showAccuracyDirection: true,
  },
  {
    key: "averageScore",
    label: "Average Score",
    desc: "Points per quiz",
    icon: <StarFilled />,
    iconBg: "#fffbeb", iconColor: "#d97706", accentColor: "#d97706",
    format: (v) => v ?? "—",
    showTotalContext: true,
  },
];

const INSIGHTS_KPIs = [
  {
    key: "strongestSpeciality",
    label: "Strongest Specialty",
    desc: "Highest accuracy in period",
    icon: <RiseOutlined />,
    iconBg: "#f0fdf4", iconColor: "#16a34a", accentColor: "#16a34a",
    format: (v) => v ?? "—",
    smallValue: true,
    showSpecialtyCounts: true,
  },
  {
    key: "weakestSpeciality",
    label: "Weakest Specialty",
    desc: "Lowest accuracy in period",
    icon: <FallOutlined />,
    iconBg: "#fff1f2", iconColor: "#dc2626", accentColor: "#dc2626",
    format: (v) => v ?? "—",
    smallValue: true,
    showSpecialtyCounts: true,
  },
  {
    key: "__passStreak__",
    label: "Passing Quiz Streak",
    desc: "Consecutive passing quizzes",
    icon: <FireOutlined />,
    iconBg: "#fff7ed", iconColor: "#ea580c", accentColor: "#ea580c",
    format: (v) => v != null ? `${v} quiz${v !== 1 ? "zes" : ""}` : "—",
    smallValue: true,
    isStreak: true,
  },
];

// ── Skeleton ───────────────────────────────────────────────────────────────
const KpiSkeleton = () => (
  <Card className={styles.sectionCard}>
    <div className={styles.sectionHeader}>
      <Skeleton.Input active size="small" className={styles.skeletonTitle} />
      <Skeleton.Input active size="small" className={styles.skeletonFilter} />
    </div>
    <Row gutter={[10, 10]}>
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <Col key={i} xs={24} sm={12} md={8}>
          <Card className={styles.kpiCard} style={{ borderTopColor: "#e6f0ff" }}>
            <Skeleton.Input active size="small" className={styles.skeletonKpiBody} />
            <Skeleton.Input active size="large" className={styles.skeletonKpiVal} />
            <Skeleton.Input active size="small" className={styles.skeletonKpiDesc} />
          </Card>
        </Col>
      ))}
    </Row>
  </Card>
);

// ── Main component ─────────────────────────────────────────────────────────
const DashboardKPIsV2 = ({
  stats,
  passStreak = 0,
  loading,
  isFetching,
  dateRange,
  onDateRangeChange,
  enrichData = {},
}) => {
  if (loading) return <KpiSkeleton />;

  const dataMap = { ...stats, __passStreak__: passStreak };
  const ALL_KPIs = [...METRICS_KPIs, ...INSIGHTS_KPIs];

  const dateLabel =
    dateRange?.[0] && dateRange?.[1]
      ? `${dateRange[0].format("MMM D")} – ${dateRange[1].format("MMM D, YY")}`
      : "All time";

  return (
    <Card className={styles.sectionCard}>
      <div className={styles.cardContentWrap}>

        {/* ── Single header: title left, date filter right ── */}
        <div className={styles.sectionHeader}>
          <div className={styles.sectionTitleGroup}>
            <Title level={5} className={styles.sectionTitle}>
              Key Performance Metrics
            </Title>
            <span className={styles.sectionSubtitle}>{dateLabel} — all cards respond to this filter</span>
          </div>
          <div className={styles.filterWrap}>
            <span className={styles.filterLabel}>Filter period</span>
            <Tooltip
              title="Sends startDate & endDate to both the stats and performance-trend APIs. Passing Quiz Streak is computed client-side from returned quiz data."
              placement="bottomRight"
            >
              <InfoCircleOutlined style={{ color: "#94a3b8", fontSize: 11, cursor: "pointer" }} />
            </Tooltip>
            <RangePicker
              size="small"
              value={dateRange}
              ranges={DATE_PRESETS}
              format="MMM D, YY"
              allowClear
              onChange={onDateRangeChange}
              placeholder={["Start date", "End date"]}
            />
          </div>
        </div>

        {/* ── Unified 6-card grid ── */}
        <Row gutter={[10, 10]}>
          {ALL_KPIs.map((kpi) => (
            <Col key={kpi.key} xs={24} sm={12} md={8}>
              <KpiCard kpi={kpi} value={dataMap[kpi.key]} enrichData={enrichData} />
            </Col>
          ))}
        </Row>

        {isFetching && <div className={styles.shimmerOverlay} />}
      </div>
    </Card>
  );
};

export default DashboardKPIsV2;
